import { useEffect, useMemo, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Building2, ChevronDown, LogOut, Sparkles, User as UserIcon } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useAiReportAvailability } from "../context/AiReportAvailabilityContext";
import { adminMenu, NavItem, userMenu } from "../nav/config";
import HoverMenu from "./HoverMenu";
import DemoAnalyticsNotice from "./DemoAnalyticsNotice";
import SubscriptionNotice from "./SubscriptionNotice";

function filterMainMenu(items: NavItem[]): NavItem[] {
  return items
    .map((item) =>
      item.children
        ? {
            ...item,
            children: item.children.filter(
              (child) => child.label !== "Профиль" && child.label !== "Выход",
            ),
          }
        : item,
    )
    .filter((item) => item.label !== "Профиль" && item.label !== "Выход");
}

export default function Topbar() {
  const hasAiReports = useAiReportAvailability();
  const {
    user,
    logout,
    availableCompanies,
    selectedCompanyId,
    setSelectedCompanyId,
  } = useAuth();
  const location = useLocation();
  const isAnalyticsActive = location.pathname.startsWith("/analytics");
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onDocClick = (event: MouseEvent) => {
      if (!wrapRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    document.addEventListener("click", onDocClick);
    document.addEventListener("keydown", onKey);

    return () => {
      document.removeEventListener("click", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  const fullMenu = useMemo(
    () => (user ? (user.role === "admin" ? adminMenu(logout) : userMenu(logout)) : []),
    [user, logout],
  );
  const mainMenu = useMemo(() => filterMainMenu(fullMenu), [fullMenu]);
  const showCompanySelect = Boolean(
    user &&
    user.role !== "admin" &&
    (user.multi_account_type || availableCompanies.length > 1),
  );

  return (
    <div className="sticky top-0 z-30">
      <header
        className="border-b border-slate-100 bg-white/80 backdrop-blur before:pointer-events-none before:absolute before:inset-0 before:bg-gradient-to-b before:from-indigo-200/20 before:to-indigo-50/40 before:opacity-0 before:transition-all before:duration-500 hover:before:opacity-100"
      >
        <div className="mx-auto flex max-w-[1800px] items-center justify-between px-7 py-4">
        <Link
          to="/"
          className="bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-2xl font-semibold text-transparent animate-softFade"
        >
          TravelStat
        </Link>

        {user ? (
          <div className="flex items-center gap-3">
            {showCompanySelect && (
              <label className="relative hidden min-w-[220px] max-w-[320px] items-center text-sm text-slate-700 lg:flex">
                <Building2
                  size={17}
                  className="pointer-events-none absolute left-3 text-slate-400"
                />
                <select
                  value={selectedCompanyId ?? ""}
                  onChange={(event) => setSelectedCompanyId(event.target.value ? Number(event.target.value) : null)}
                  className="h-11 w-full appearance-none truncate rounded-2xl border border-slate-200 bg-white py-2 pl-10 pr-9 text-sm shadow-sm outline-none transition hover:bg-slate-50 focus:border-indigo-300 focus:ring-4 focus:ring-indigo-100"
                >
                  {availableCompanies.map((company) => (
                    <option key={company.id} value={company.id}>
                      {company.company_name}
                    </option>
                  ))}
                </select>
                <ChevronDown
                  size={17}
                  className="pointer-events-none absolute right-3 text-slate-400"
                />
              </label>
            )}

            {hasAiReports && <Link
              to="/ai-report"
              className="group/ai hidden items-center gap-2 rounded-full border border-indigo-200/80 bg-gradient-to-r from-indigo-50 via-white to-cyan-50 px-3 py-2 text-xs font-semibold text-indigo-700 shadow-[0_0_18px_rgba(99,102,241,0.18)] transition-all duration-300 hover:-translate-y-0.5 hover:border-indigo-300 hover:shadow-[0_0_26px_rgba(99,102,241,0.32)] lg:inline-flex"
            >
              <Sparkles size={15} className="text-indigo-500 transition-transform duration-300 group-hover/ai:rotate-12 group-hover/ai:scale-110" />
              <span>ИИ-отчёт</span>
            </Link>}

            <nav className="hidden items-center gap-1 md:flex">
              {mainMenu.map((item, idx) => {
                if (item.children && item.label === "Аналитика") {
                  const button = (
                    <button
                      className={`relative inline-flex items-center gap-1.5 rounded-xl px-3 py-2 transition
${isAnalyticsActive ? "bg-indigo-50 text-indigo-700 before:absolute before:inset-0 before:rounded-xl before:bg-indigo-200/20 before:blur-lg" : ""}
after:absolute after:bottom-1 after:left-3 after:right-3 after:h-[2px]
after:rounded-full after:bg-indigo-500 after:transition-all after:duration-300
${isAnalyticsActive ? "after:scale-100 after:opacity-100" : "after:scale-50 after:opacity-0 hover:bg-slate-100"}`}
                    >
                      {item.icon ? <item.icon size={18} /> : null}
                      <span className="text-sm">{item.label}</span>
                    </button>
                  );

                  return (
                    <HoverMenu key={idx} button={button} align="left">
                      <div className="bg-gradient-to-b from-white to-indigo-50/40">
                        {(item.children ?? []).map((child, i) => (
                          child.to ? (
                            <NavLink
                              key={i}
                              to={child.to}
                              className={({ isActive }) =>
                                `flex items-center gap-2 px-4 py-2.5 text-[0.98rem] hover:bg-slate-50 ${isActive ? "bg-indigo-50 text-indigo-700" : ""}`
                              }
                            >
                              {child.icon ? <child.icon size={16} className="text-indigo-600" /> : null}
                              {child.label}
                            </NavLink>
                          ) : null
                        ))}
                      </div>
                    </HoverMenu>
                  );
                }

                return item.to ? (
                  <NavLink
                    key={idx}
                    to={item.to}
                    className={({ isActive }) =>
                      `group relative inline-flex items-center gap-2 rounded-xl px-3 py-2 transition
${isActive ? "bg-indigo-50 text-indigo-700 before:absolute before:inset-0 before:rounded-xl before:bg-indigo-200/20 before:blur-lg" : ""}
after:absolute after:bottom-1 after:left-3 after:right-3 after:h-[2px]
after:rounded-full after:bg-indigo-500 after:transition-all after:duration-300
${isActive ? "after:scale-100 after:opacity-100" : "after:scale-50 after:opacity-0 hover:bg-slate-100"}`
                    }
                  >
                    {item.icon ? (
                      <item.icon
                        size={18}
                        className="text-slate-700 transition-transform duration-300 group-hover:scale-110"
                      />
                    ) : null}
                    <span className="text-sm">{item.label}</span>
                  </NavLink>
                ) : null;
              })}
            </nav>

            <div ref={wrapRef} className="relative">
              <button
                type="button"
                aria-haspopup="menu"
                aria-expanded={open}
                onClick={() => setOpen((value) => !value)}
                className="flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 hover:bg-slate-50"
              >
                <span className="inline-grid h-9 w-9 place-items-center rounded-full bg-slate-100 text-slate-600">
                  <UserIcon size={20} />
                </span>
                <span className="hidden text-[0.98rem] text-slate-700 sm:block">{user.email}</span>
                <ChevronDown
                  size={18}
                  className={`text-slate-500 transition ${open ? "rotate-180" : ""}`}
                />
              </button>

              <div
                role="menu"
                className={`absolute right-0 mt-3 w-72 origin-top overflow-hidden rounded-3xl border border-white/60 bg-white/80 shadow-[0_20px_40px_rgba(0,0,0,0.18)] backdrop-blur-2xl transition-all duration-300
${open ? "translate-y-0 scale-100 opacity-100" : "pointer-events-none -translate-y-2 scale-95 opacity-0"}`}
              >
                <NavLink
                  to="/account"
                  onClick={() => setOpen(false)}
                  className="group flex items-center gap-3 px-5 py-3 text-sm transition hover:bg-indigo-50 hover:text-indigo-700"
                  role="menuitem"
                >
                  <UserIcon
                    size={16}
                    className="text-slate-600 transition-transform duration-300 group-hover:scale-110"
                  />
                  Профиль
                </NavLink>
                <button
                  onClick={logout}
                  className="group flex w-full items-center gap-3 px-5 py-3 text-left text-sm text-red-600 transition hover:bg-red-50 hover:text-red-700"
                  role="menuitem"
                >
                  <LogOut size={16} className="transition-transform duration-300 group-hover:scale-110" />
                  Выход
                </button>
              </div>
            </div>
          </div>
        ) : (
          <Link
            to="/auth/login"
            className="inline-flex items-center justify-center rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 px-5 py-2.5 text-sm font-semibold text-white shadow-md transition hover:opacity-90 hover:shadow-lg"
          >
            Войти
          </Link>
        )}
        </div>
      </header>
      <SubscriptionNotice />
      <DemoAnalyticsNotice />
    </div>
  );
}
